/** Homepage copy — structure inspired by Rock Health (rockhealth.org). */

/** Header wordmark — NeedHealth (black) + .Care (green) */
export const BRAND_LOGO = {
  needHealth: "NeedHealth",
  care: ".Care",
  ariaLabel: "NeedHealth.Care home",
};

/** Green utility bar — Need Healthcare (Advisory | Capital | NeedHealth.Care) */
export const HEADER_UTILITY_NAV = [
  { label: "Advisory", section: "mission" },
  { label: "Capital", to: "/", utilityActive: true },
  { label: "NeedHealth.Care", to: "/" },
];

/** Primary nav — same labels & order as rockhealthcapital.com */
export const NAV_SECTIONS = [
  { label: "Sign in", to: "/login" },
  { label: "Job Board", section: "opportunities" },
  { label: "Insights", section: "insights" },
  { label: "About Us", section: "mission" },
  { to: "/signup", label: "Open account" },
];

export const JOIN_PORTFOLIO = {
  title: "Join Our Portfolio",
  lead: "We've built a portfolio of exceptional businesses tackling daunting challenges across healthcare.",
  sub: "Join us—follow the link below to submit your information.",
  cta: "Open account",
  signInCta: "Already have an account? Sign in",
  newsletterTitle: "Get the Need Healthcare Weekly.",
  newsletterArchive: "Browse the archive",
};

export const HERO = {
  title: "Need Healthcare",
  lead:
    "We connect capital to remarkable healthcare assets at the intersection of care delivery, technology, and disciplined investment.",
};

export const MISSION = {
  title: "Shaping the future of healthcare capital",
  paragraphs: [
    "We drive disciplined capital formation where healthcare assets need funding and technology can deliver measurable outcomes for patients and investors alike.",
    "Our goals are twofold: to increase investment across verified healthcare projects and to expand transparent adoption of platform tools — wallet, share ledger, and certificates — so underserved care markets can access professional capital.",
  ],
  highlightPhrases: [
    { text: "verified healthcare projects", section: "opportunities" },
    { text: "transparent adoption", section: "platform" },
  ],
};

export const IMPACT_PILLARS = [
  {
    id: "projects",
    icon: "projects",
    title: "Verified project catalog",
    text: "We list **hospitals, clinics, and diagnostic assets** through structured fundraising, funding, and maturity stages — with live progress visible to every investor.",
    statKey: "active_projects",
    statLabel: "active projects",
  },
  {
    id: "wallet",
    icon: "wallet",
    title: "BDT wallet & share ledger",
    text: "We operate a single **audited wallet** for deposits, share purchases, transfer settlements, and redemptions — every movement labeled and timestamped.",
    statKey: "total_funds_raised_bdt",
    statLabel: "capital deployed (BDT)",
    statFormat: "funds",
  },
  {
    id: "certificates",
    icon: "certificates",
    title: "Ownership & certificates",
    text: "Investors receive **share certificates** with verifiable records aligned to NAV-based holdings, transfers, and redemption workflows.",
    statKey: "total_investors",
    statLabel: "registered investors",
  },
  {
    id: "governance",
    icon: "governance",
    title: "Governance & compliance",
    text: "Administrators and asset managers work inside the same system — with **OTP verification**, admin-reviewed deposits, and compliance-ready audit trails.",
    statKey: "success_rate_pct",
    statLabel: "funding success rate",
    statFormat: "percent",
  },
];

export const IMPACT_CLOSING =
  "Through these efforts, Need Healthcare is cultivating, supporting, and investing in healthcare ecosystems to transform how capital reaches care. We are Need Healthcare — and we are shaping the future of healthcare investment.";

export const PLATFORM_PROGRAMS = [
  {
    id: "portal",
    featured: true,
    eyebrow: "Investor access",
    title: "Investor portal",
    description:
      "Portfolio overview, wallet, share management, financial history, and notices — one secure workspace for professional investors.",
    cta: "Sign in",
    to: "/login",
  },
  {
    id: "onboard",
    title: "Open your account",
    description:
      "Structured onboarding with email verification, referral support, and immediate access to the live project catalog.",
    cta: "Open account",
    to: "/signup",
  },
  {
    id: "invest",
    title: "Invest in healthcare",
    description:
      "Purchase shares during fundraising, monitor funding progress, and track indicative returns across your holdings.",
    cta: "View details",
    section: "opportunities",
  },
  {
    id: "certificates",
    title: "Share certificates",
    description:
      "Generate certificate PDFs per holding and verify ownership through secure, token-based public verification.",
    cta: "View details",
    to: "/login",
  },
];

export const PLATFORM_INSIGHTS = [
  {
    category: "Platform snapshot",
    title: "Healthcare capital on a single operating system",
    excerpt:
      "Wallet, project fundraising, share ledger, transfers, redemptions, and reporting — designed for clarity, not complexity.",
    section: "platform",
  },
  {
    category: "Market view",
    title: "Why healthcare remains a resilient allocation",
    excerpt:
      "Demographics, chronic demand, and innovation cycles support long-horizon strategies — with transparent funding models on-platform.",
    section: "mission",
  },
  {
    category: "Investor guide",
    title: "From discovery to certificate-backed ownership",
    excerpt:
      "Three steps: discover verified projects, invest via wallet, and track portfolio performance in real time.",
    section: "platform",
  },
  {
    category: "Security",
    title: "Built for regulated-grade trust",
    excerpt:
      "Encryption, KYC-ready profiles, verified partners, and transparent fund allocation — formal controls for professional investors.",
    section: "platform",
  },
  {
    category: "Deep dive",
    title: "Share transfers and redemptions at NAV",
    excerpt:
      "Secondary-style transfers and structured redemptions with fee preview and admin oversight where required.",
    section: "platform",
  },
  {
    category: "Impact",
    title: "Capital with purpose in emerging healthcare markets",
    excerpt:
      "Connect professional investors to hospitals and clinics where disciplined capital can expand access to care.",
    section: "platform",
  },
];

export const STAY_CONNECTED = {
  title: "Stay in the loop.",
  lead: "Open an investor account to access the live catalog, wallet, and portfolio tools. We will keep you informed as new healthcare projects publish.",
  cta: "Open account",
  to: "/signup",
  secondaryCta: "Sign in",
  secondaryTo: "/login",
};

